// @ts-nocheck
import React, { useEffect, useRef } from "react";
import * as THREE from "three/webgpu";
import {
  atan,
  cos,
  float,
  max,
  min,
  mix,
  PI,
  TWO_PI,
  sin,
  vec2,
  vec3,
  color,
  Fn,
  hash,
  hue,
  If,
  instanceIndex,
  Loop,
  mx_fractal_noise_float,
  mx_fractal_noise_vec3,
  pcurve,
  storage,
  deltaTime,
  time,
  uv,
  uniform,
  step,
} from "three/tsl";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const nbParticles = Math.pow(2, 11);

export const BackgroundLinkedParticles: React.FC = () => {
  const mountRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    if (!("gpu" in navigator)) {
      // eslint-disable-next-line no-console
      console.warn(
        "WebGPU not supported in this browser – skipping linked particles background.",
      );
      return;
    }

    // --- uniforms / config ---
    const timeScale = uniform(0.5); // <== slower motion (0.5 = half speed)
    const particleLifetime = uniform(0.5);
    const particleSize = uniform(1.0);
    const linksWidth = uniform(0.005);

    const colorOffset = uniform(0.0);
    const colorVariance = uniform(2.0);
    const colorRotationSpeed = uniform(1.0);

    const spawnIndex = uniform(0);
    const nbToSpawn = uniform(1);
    const spawnPosition = uniform(vec3(0.0));
    const previousSpawnPosition = uniform(vec3(0.0));

    const turbFrequency = uniform(0.5);
    const turbAmplitude = uniform(0.5);
    const turbOctaves = uniform(2);
    const turbLacunarity = uniform(2.0);
    const turbGain = uniform(0.5);
    const turbFriction = uniform(0.01);

    // pointer / raycast stuff
    const screenPointer = new THREE.Vector2();
    const scenePointer = new THREE.Vector3();
    const raycastPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const raycaster = new THREE.Raycaster();

    let camera: THREE.PerspectiveCamera;
    let scene: THREE.Scene;
    let renderer: THREE.WebGPURenderer;
    let controls: OrbitControls;
    let timer: THREE.Timer;

    let updateParticles: any;
    let spawnParticles: any;

    let animationId: number | null = null;
    let disposed = false;

    const onPointerMove = (e: PointerEvent) => {
      screenPointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      screenPointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    const updatePointer = () => {
      raycaster.setFromCamera(screenPointer, camera);
      raycaster.ray.intersectPlane(raycastPlane, scenePointer);
    };

    const onWindowResize = () => {
      const { innerWidth, innerHeight } = window;
      camera.aspect = innerWidth / innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(innerWidth, innerHeight);
    };

    const init = async () => {
      const { innerWidth, innerHeight } = window;

      // --- camera ---
      camera = new THREE.PerspectiveCamera(
        60,
        innerWidth / innerHeight,
        0.1,
        200,
      );
      camera.position.set(0, 0, 10);

      // --- scene ---
      scene = new THREE.Scene();
      // IMPORTANT: no scene.background and no background geometry → fully transparent canvas

      // --- timer ---
      timer = new THREE.Timer();
      timer.connect(document);

      // --- renderer (transparent) ---
      renderer = new THREE.WebGPURenderer({ antialias: true, alpha: true });
      // alpha = 0 → transparent
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(innerWidth, innerHeight);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;

      container.appendChild(renderer.domElement);
      // extra safety – make sure canvas itself is transparent
      renderer.domElement.style.background = "transparent";

      await renderer.init();

      // --- TSL function: color per particle index ---
      const getInstanceColor = Fn((inputs) => {
        const i = inputs[0];

        // vivid green → tweak if you want different color
        return hue(
          color(0x00ff66),
          colorOffset.add(
            mx_fractal_noise_float(
              i.toFloat().mul(0.1),
              2,
              2.0,
              0.5,
              colorVariance,
            ),
          ),
        );
      });

      // --- storage buffers ---
      const particlePositions = storage(
        new THREE.StorageInstancedBufferAttribute(nbParticles, 4),
        "vec4",
        nbParticles,
      );
      const particleVelocities = storage(
        new THREE.StorageInstancedBufferAttribute(nbParticles, 4),
        "vec4",
        nbParticles,
      );

      // init particles far away and "dead"
      renderer.compute(
        Fn(() => {
          particlePositions.element(instanceIndex).xyz.assign(vec3(10000.0));
          // w < 0 = dead
          particlePositions.element(instanceIndex).w.assign(vec3(-1.0));
        })().compute(nbParticles),
      );

      // --- particles geometry ---
      const particleQuadSize = 0.05;
      const particleGeom = new THREE.PlaneGeometry(
        particleQuadSize,
        particleQuadSize,
      );

      const particleMaterial = new THREE.SpriteNodeMaterial();
      particleMaterial.blending = THREE.AdditiveBlending;
      particleMaterial.depthWrite = false;
      particleMaterial.depthTest = true;
      particleMaterial.positionNode = particlePositions.toAttribute();
      particleMaterial.scaleNode = vec2(particleSize);
      particleMaterial.rotationNode = atan(
        particleVelocities.toAttribute().y,
        particleVelocities.toAttribute().x,
      );

      particleMaterial.colorNode = Fn(() => {
        const life = particlePositions.toAttribute().w;
        const modLife = pcurve(life.oneMinus(), 8.0, 1.0);
        const pulse = pcurve(
          sin(hash(instanceIndex).mul(TWO_PI).add(time.mul(0.5).mul(TWO_PI)))
            .mul(0.5)
            .add(0.5),
          0.25,
          0.25,
        )
          .mul(10.0)
          .add(1.0);

        return getInstanceColor(instanceIndex).mul(pulse.mul(modLife));
      })();

      particleMaterial.opacityNode = Fn(() => {
        const circle = step(uv().xy.sub(0.5).length(), 0.5);
        const life = particlePositions.toAttribute().w;
        return circle.mul(life);
      })();

      const particleMesh = new THREE.InstancedMesh(
        particleGeom,
        particleMaterial,
        nbParticles,
      );
      particleMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      particleMesh.frustumCulled = false;
      scene.add(particleMesh);

      // --- links setup ---
      const linksIndices: number[] = [];
      for (let i = 0; i < nbParticles; i++) {
        const baseIndex = i * 8;
        for (let j = 0; j < 2; j++) {
          const offset = baseIndex + j * 4;
          linksIndices.push(
            offset,
            offset + 1,
            offset + 2,
            offset,
            offset + 2,
            offset + 3,
          );
        }
      }

      const nbVertices = nbParticles * 8;
      const linksVerticesSBA = new THREE.StorageBufferAttribute(nbVertices, 4);
      const linksColorsSBA = new THREE.StorageBufferAttribute(nbVertices, 4);

      const linksGeom = new THREE.BufferGeometry();
      linksGeom.setAttribute("position", linksVerticesSBA);
      linksGeom.setAttribute("color", linksColorsSBA);
      linksGeom.setIndex(linksIndices);

      const linksMaterial = new THREE.MeshBasicNodeMaterial();
      linksMaterial.vertexColors = true;
      linksMaterial.side = THREE.DoubleSide;
      linksMaterial.transparent = true;
      linksMaterial.depthWrite = false;
      linksMaterial.depthTest = false;
      linksMaterial.blending = THREE.AdditiveBlending;
      linksMaterial.opacityNode = storage(
        linksColorsSBA,
        "vec4",
        linksColorsSBA.count,
      ).toAttribute().w;

      const linksMesh = new THREE.Mesh(linksGeom, linksMaterial);
      linksMesh.frustumCulled = false;
      scene.add(linksMesh);

      // --- compute: updateParticles ---
      updateParticles = Fn(() => {
        const position = particlePositions.element(instanceIndex).xyz;
        const life = particlePositions.element(instanceIndex).w;
        const velocity = particleVelocities.element(instanceIndex).xyz;
        const dt = deltaTime.mul(0.1).mul(timeScale);

        If(life.greaterThan(0.0), () => {
          const localVel = mx_fractal_noise_vec3(
            position.mul(turbFrequency),
            turbOctaves,
            turbLacunarity,
            turbGain,
            turbAmplitude,
          ).mul(life.add(0.01));
          velocity.addAssign(localVel);
          velocity.mulAssign(turbFriction.oneMinus());
          position.addAssign(velocity.mul(dt));

          life.subAssign(dt.mul(particleLifetime.reciprocal()));

          const closestDist1 = float(10000.0).toVar();
          const closestPos1 = vec3(0.0).toVar();
          const closestLife1 = float(0.0).toVar();
          const closestDist2 = float(10000.0).toVar();
          const closestPos2 = vec3(0.0).toVar();
          const closestLife2 = float(0.0).toVar();

          Loop(nbParticles, ({ i }) => {
            const otherPart = particlePositions.element(i);
            If(
              i.notEqual(instanceIndex).and(otherPart.w.greaterThan(0.0)),
              () => {
                const otherPosition = otherPart.xyz;
                const dist = position.sub(otherPosition).lengthSq();
                const moreThanZero = dist.greaterThan(0.0);

                If(dist.lessThan(closestDist1).and(moreThanZero), () => {
                  closestDist1.assign(dist);
                  closestPos1.assign(otherPosition.xyz);
                  closestLife1.assign(otherPart.w);
                }).ElseIf(dist.lessThan(closestDist2).and(moreThanZero), () => {
                  closestDist2.assign(dist);
                  closestPos2.assign(otherPosition.xyz);
                  closestLife2.assign(otherPart.w);
                });
              },
            );
          });

          const linksPositions = storage(
            linksVerticesSBA,
            "vec4",
            linksVerticesSBA.count,
          );
          const linksColors = storage(
            linksColorsSBA,
            "vec4",
            linksColorsSBA.count,
          );
          const firstLinkIndex = instanceIndex.mul(8);
          const secondLinkIndex = firstLinkIndex.add(4);

          // link 1
          linksPositions.element(firstLinkIndex).xyz.assign(position);
          linksPositions.element(firstLinkIndex).y.addAssign(linksWidth);
          linksPositions.element(firstLinkIndex.add(1)).xyz.assign(position);
          linksPositions
            .element(firstLinkIndex.add(1))
            .y.addAssign(linksWidth.negate());
          linksPositions.element(firstLinkIndex.add(2)).xyz.assign(closestPos1);
          linksPositions
            .element(firstLinkIndex.add(2))
            .y.addAssign(linksWidth.negate());
          linksPositions.element(firstLinkIndex.add(3)).xyz.assign(closestPos1);
          linksPositions.element(firstLinkIndex.add(3)).y.addAssign(linksWidth);

          // link 2
          linksPositions.element(secondLinkIndex).xyz.assign(position);
          linksPositions.element(secondLinkIndex).y.addAssign(linksWidth);
          linksPositions.element(secondLinkIndex.add(1)).xyz.assign(position);
          linksPositions
            .element(secondLinkIndex.add(1))
            .y.addAssign(linksWidth.negate());
          linksPositions
            .element(secondLinkIndex.add(2))
            .xyz.assign(closestPos2);
          linksPositions
            .element(secondLinkIndex.add(2))
            .y.addAssign(linksWidth.negate());
          linksPositions
            .element(secondLinkIndex.add(3))
            .xyz.assign(closestPos2);
          linksPositions
            .element(secondLinkIndex.add(3))
            .y.addAssign(linksWidth);

          const linkColor = getInstanceColor(instanceIndex);

          const l1 = max(0.0, min(closestLife1, life)).pow(0.8);
          const l2 = max(0.0, min(closestLife2, life)).pow(0.8);

          Loop(4, ({ i }) => {
            linksColors.element(firstLinkIndex.add(i)).xyz.assign(linkColor);
            linksColors.element(firstLinkIndex.add(i)).w.assign(l1);
            linksColors.element(secondLinkIndex.add(i)).xyz.assign(linkColor);
            linksColors.element(secondLinkIndex.add(i)).w.assign(l2);
          });
        });
      })()
        .compute(nbParticles)
        .setName("Update Particles");

      // --- compute: spawnParticles ---
      spawnParticles = Fn(() => {
        const particleIndex = spawnIndex
          .add(instanceIndex)
          .mod(nbParticles)
          .toInt();
        const position = particlePositions.element(particleIndex).xyz;
        const life = particlePositions.element(particleIndex).w;
        const velocity = particleVelocities.element(particleIndex).xyz;

        life.assign(1.0);

        const rRange = float(0.01);
        const rTheta = hash(particleIndex).mul(TWO_PI);
        const rPhi = hash(particleIndex.add(1)).mul(PI);
        const rx = sin(rTheta).mul(cos(rPhi));
        const ry = sin(rTheta).mul(sin(rPhi));
        const rz = cos(rTheta);
        const rDir = vec3(rx, ry, rz);

        const pos = mix(
          previousSpawnPosition,
          spawnPosition,
          instanceIndex.toFloat().div(nbToSpawn.sub(1).toFloat()).clamp(),
        );
        position.assign(pos.add(rDir.mul(rRange)));
        velocity.assign(rDir.mul(5.0));
      })()
        .compute(nbToSpawn.value)
        .setName("Spawn Particles");

      // --- controls ---
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.autoRotate = true;
      controls.maxDistance = 75;

      window.addEventListener("resize", onWindowResize);
      window.addEventListener("pointermove", onPointerMove);

      // --- animation loop ---
      const animate = () => {
        if (disposed) return;

        timer.update();

        renderer.compute(updateParticles);
        renderer.compute(spawnParticles);

        spawnIndex.value = (spawnIndex.value + nbToSpawn.value) % nbParticles;

        raycastPlane.normal.applyEuler(camera.rotation);
        updatePointer();

        previousSpawnPosition.value.copy(spawnPosition.value);
        spawnPosition.value.lerp(scenePointer, 0.1);

        colorOffset.value +=
          timer.getDelta() * colorRotationSpeed.value * timeScale.value;

        controls.update();
        renderer.render(scene, camera); // <== direct render, no background, no post-processing

        animationId = requestAnimationFrame(animate);
      };

      animate();
    };

    init();

    return () => {
      disposed = true;
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("pointermove", onPointerMove);
      if (animationId !== null) cancelAnimationFrame(animationId);

      if (renderer) {
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2,
        overflow: "hidden",
        pointerEvents: "none", // don’t block your login form
        backgroundColor: "transparent",
      }}
    />
  );
};
